// React, Next
import { ReactElement, useContext } from 'react'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useRouter } from 'next/router'

// Mui
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Custom
import { GlobalContext } from 'context/GlobalContextProvider'
import { PageType, translateHref } from 'assets/functions/links'
import SocialMediaIconButton from '@/components/tools/buttons/socialMedia/SocialMediaIconButton'

// Config
import i18nextConfig from 'next-i18next.config'

// Style
import breakpoints from '@/assets/theme/breakpoints'
import classes from './MainFooter-CSS'

type FooterLink = {
  name: string
  href: PageType
}

export default function MainFooter() {
  const { screenDimensions } = useContext(GlobalContext)
  const { t } = useTranslation('common')
  const router = useRouter()
  const currentLocale = router.query.locale || i18nextConfig.i18n.defaultLocale

  const width = screenDimensions.width

  const linkColumns: { title: string; links: FooterLink[] }[] = [
    {
      title: t('menu.solutions'),
      links: [
        { name: t('subMenu.solutions4'), href: 'iot-air-quality-monitoring' }
      ]
    },
    {
      title: t('menu.services'),
      links: [
        { name: t('subMenu.services6'), href: 'ot-cybersecurity' },
        { name: t('subMenu.services7'), href: 'ot-awareness-training' },
        { name: t('subMenu.services8'), href: 'test-equipment-modernization' }
      ]
    },
    {
      title: t('menu.company'),
      links: [
        { name: t('subMenu.company1'), href: 'technology' },
        { name: t('subMenu.company3'), href: 'about' },
        { name: t('subMenu.company2'), href: 'contact' },
        { name: t('footer.articles'), href: 'articles' }
      ]
    },
    {
      title: t('footer.legal'),
      links: [
        { name: t('footer.privacy'), href: 'privacy' },
        { name: t('footer.legalNotice'), href: 'legal-notice' },
        { name: t('footer.termsOfUse'), href: 'terms-of-use' }
      ]
    }
  ]

  // Six columns only fit comfortably on a laptop and up. Below that the grid
  // steps down rather than squeezing, so no column ever wraps mid-word.
  const getGridColumns = () => {
    if (width > breakpoints.desktopSmall) {
      return '1.4fr 1.3fr 1fr 1.5fr 1.1fr 1.2fr'
    }
    if (width > breakpoints.desktopToMobile) return 'repeat(3, 1fr)'
    if (width > breakpoints.mobileMid) return 'repeat(2, 1fr)'
    return '1fr'
  }

  const getPaddingX = () => {
    if (width > breakpoints.desktopSmall) return '8%'
    if (width > breakpoints.desktopToMobile) return '6%'
    return '1.5rem'
  }

  const getColumn = (title: string, content: ReactElement) => (
    <Box>
      <Typography component="h3" sx={classes.columnTitle}>
        {title}
      </Typography>
      {content}
    </Box>
  )

  const getCompanyColumn = () =>
    getColumn(
      'Kroemtech GmbH',
      <>
        <Typography component="p" sx={classes.body}>
          Christoph Merian-Ring 11
          <br />
          CH-4153 Reinach
          <br />
          {t('footer.ch')}
        </Typography>
        <Box sx={classes.socialBox}>
          <SocialMediaIconButton
            outlined
            color="#fff"
            socialMedia="facebook"
            tooltip="Facebook"
            href="https://www.facebook.com/kroemtech/"
          />
          <SocialMediaIconButton
            outlined
            color="#fff"
            socialMedia="instagram"
            tooltip="Instagram"
            href="https://www.instagram.com/kroemtech/"
          />
          <SocialMediaIconButton
            outlined
            color="#fff"
            socialMedia="linkedin"
            tooltip="Linkedin"
            href="https://www.linkedin.com/company/kroemtech"
          />
        </Box>
      </>
    )

  const getContactColumn = () =>
    getColumn(
      t('footer.contact'),
      <>
        <Typography component="p" sx={classes.body}>
          <Box component="span" sx={{ marginRight: '0.75rem' }}>
            T
          </Box>
          +41 61 717 82 45
        </Typography>
        <Box
          component="a"
          href="mailto:info@kroemtech.com"
          sx={{ ...classes.link, ...classes.block }}
        >
          info@kroemtech.com
        </Box>
        <Typography component="p" sx={{ ...classes.body, ...classes.block }}>
          {t('footer.officeDays')}
          <br />
          {t('footer.officeMorning')}
          <br />
          {t('footer.officeAfternoon')}
        </Typography>
      </>
    )

  const getLinkColumn = (column: { title: string; links: FooterLink[] }) =>
    getColumn(
      column.title,
      <Box sx={classes.linkList}>
        {column.links.map(item => (
          <Link
            key={item.href}
            href={translateHref(currentLocale, item.href) ?? '#'}
            style={{ textDecoration: 'none' }}
          >
            <Box component="span" sx={classes.link}>
              {item.name}
            </Box>
          </Link>
        ))}
      </Box>
    )

  // *** MAIN RENDER ***
  return (
    <Box
      component="footer"
      sx={{
        ...classes.mainBox,
        paddingLeft: getPaddingX(),
        paddingRight: getPaddingX()
      }}
    >
      <Box
        sx={{
          ...classes.grid,
          gridTemplateColumns: getGridColumns()
        }}
      >
        {getCompanyColumn()}
        {getContactColumn()}
        {linkColumns.map(column => (
          <Box key={column.title}>{getLinkColumn(column)}</Box>
        ))}
      </Box>

      <Box sx={classes.lowerBox}>
        <Typography component="p" sx={classes.copyright}>
          {`© Kroemtech GmbH – ${t('footer.allRights')}`}
        </Typography>
      </Box>
    </Box>
  )
}
